import { useEffect, useState, useCallback, useRef } from "react";
import api from "../lib/api";

/**
 * Per-user, per-restaurant dashboard preferences with a localStorage
 * fallback so the UI is instant on first paint (no skeleton waiting for
 * the network) and tolerant of brief connectivity hiccups.
 *
 * Pattern:
 *   1. On mount, read the local cache for this restaurant and apply it
 *      immediately so the Rail/KPIs render with the user's last layout.
 *   2. Fire a GET to /dashboard-preferences in the background. When it
 *      returns, merge it on top of the local cache and persist.
 *   3. setPreference(key, value) updates state + cache + PATCHes the
 *      backend. If the PATCH fails we keep the local change so the user
 *      doesn't see their toggle "snap back".
 */

const LOCAL_KEY = (rid) => `mg_dashboard_prefs_v1_${rid || "default"}`;

const DEFAULTS = {
  insight_rail_hidden: [],
};

function readLocal(rid) {
  try {
    const raw = localStorage.getItem(LOCAL_KEY(rid));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

function writeLocal(rid, prefs) {
  try {
    localStorage.setItem(LOCAL_KEY(rid), JSON.stringify(prefs));
  } catch { /* ignore quota errors */ }
}

export function useDashboardPreferences(restaurantId) {
  const [preferences, setPreferences] = useState(() => ({
    ...DEFAULTS,
    ...(readLocal(restaurantId) || {}),
  }));
  const [loading, setLoading] = useState(true);
  // Tracks the latest PATCH so a stale response can't overwrite a newer
  // local change (e.g., user toggles twice quickly).
  const lastPatchToken = useRef(0);

  // Refresh from backend whenever the restaurant changes.
  useEffect(() => {
    let cancelled = false;
    setPreferences({ ...DEFAULTS, ...(readLocal(restaurantId) || {}) });
    setLoading(true);
    if (!restaurantId) { setLoading(false); return; }
    (async () => {
      try {
        const res = await api.request(`/restaurants/${restaurantId}/dashboard-preferences`);
        if (cancelled) return;
        const merged = { ...DEFAULTS, ...(res?.preferences || {}) };
        setPreferences(merged);
        writeLocal(restaurantId, merged);
      } catch (e) {
        // Network/permission errors: stay with the local cache silently.
        console.warn("[useDashboardPreferences] load failed", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [restaurantId]);

  const setPreference = useCallback(async (key, value) => {
    // Optimistic update + local cache.
    setPreferences(prev => {
      const next = { ...prev, [key]: value };
      writeLocal(restaurantId, next);
      return next;
    });
    if (!restaurantId) return;
    const token = ++lastPatchToken.current;
    try {
      const res = await api.request(
        `/restaurants/${restaurantId}/dashboard-preferences`,
        { method: "PATCH", body: JSON.stringify({ [key]: value }), headers: { "Content-Type": "application/json" } },
      );
      // Apply server-confirmed state only if no newer PATCH was fired.
      if (token === lastPatchToken.current && res?.preferences) {
        const confirmed = { ...DEFAULTS, ...res.preferences };
        setPreferences(confirmed);
        writeLocal(restaurantId, confirmed);
      }
    } catch (e) {
      // Keep the optimistic value. The next refresh on restaurant switch
      // will reconcile if the backend ever disagrees.
      console.warn("[useDashboardPreferences] save failed", e);
    }
  }, [restaurantId]);

  const applyTemplate = useCallback(async (templateId) => {
    if (!restaurantId) return;
    const token = ++lastPatchToken.current;
    try {
      const res = await api.request(
        `/restaurants/${restaurantId}/dashboard-preferences/apply-template`,
        { method: "POST", body: JSON.stringify({ template_id: templateId }), headers: { "Content-Type": "application/json" } },
      );
      if (token === lastPatchToken.current && res?.preferences) {
        const confirmed = { ...DEFAULTS, ...res.preferences };
        setPreferences(confirmed);
        writeLocal(restaurantId, confirmed);
      }
    } catch (e) {
      console.warn("[useDashboardPreferences] applyTemplate failed", e);
    }
  }, [restaurantId]);

  return { preferences, setPreference, applyTemplate, loading };
}
